/**
 * HTTP sync for the signal plugin (main-thread fallback).
 *
 * Periodically POSTs the pre-computed LiveAnalysisRequest to the
 * backend signal endpoint.  Handles retry with exponential backoff
 * and session identity via headers.
 */

import type { LiveAnalysisRequest } from "./inference/types";

export interface SyncOptions {
  apiUrl: string;
  apiKey: string;
  syncIntervalMs: number;
  debug: boolean;
  /** Called when the backend returns a session ID. */
  onSessionId?: (id: string) => void;
  /** Called after each successful sync round-trip. */
  onSyncSuccess?: () => void;
}

export class SignalSync {
  private opts: SyncOptions;
  private sessionId: string | null = null;
  private timerId: ReturnType<typeof setTimeout> | null = null;
  private pendingRequest: LiveAnalysisRequest | null = null;
  private inflight = false;
  private consecutiveFailures = 0;
  private destroyed = false;

  constructor(opts: SyncOptions) {
    this.opts = opts;
  }

  setSessionId(id: string): void {
    this.sessionId = id;
  }

  /** Queue a request to be sent on the next sync tick. */
  enqueue(request: LiveAnalysisRequest): void {
    this.pendingRequest = request;
  }

  start(): void {
    if (this.destroyed) return;
    this.tick();
  }

  stop(): void {
    this.destroyed = true;
    if (this.timerId != null) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  /** Send any remaining data synchronously via sendBeacon on page unload. */
  flush(): void {
    if (!this.pendingRequest) return;

    const url = `${this.opts.apiUrl}/signal`;
    const body = JSON.stringify(this.pendingRequest);
    this.pendingRequest = null;

    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(url, blob);
    }
  }

  private tick(): void {
    if (this.destroyed) return;

    const delay = this.inflight
      ? this.opts.syncIntervalMs
      : Math.min(
          this.opts.syncIntervalMs * Math.pow(2, this.consecutiveFailures),
          30_000,
        );

    this.timerId = setTimeout(() => {
      this.timerId = null;
      this.send();
    }, delay);
  }

  private async send(): Promise<void> {
    if (this.destroyed) return;

    const request = this.pendingRequest;
    if (!request) {
      this.tick();
      return;
    }

    this.pendingRequest = null;
    this.inflight = true;

    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "X-Identifier-Token": this.opts.apiKey,
      };
      if (this.sessionId) {
        headers["X-Session-Id"] = this.sessionId;
      }

      const response = await fetch(`${this.opts.apiUrl}/signal`, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify(request),
      });

      if (response.ok) {
        this.consecutiveFailures = 0;

        const data = await response.json().catch(() => null);
        if (data?.sessionId && data.sessionId !== this.sessionId) {
          this.sessionId = data.sessionId;
          this.opts.onSessionId?.(data.sessionId);
        }
        this.opts.onSyncSuccess?.();
      } else {
        this.consecutiveFailures++;
        if (this.opts.debug) {
          console.warn(`[signal] Sync failed: ${response.status}`);
        }
      }
    } catch (e) {
      this.consecutiveFailures++;
      if (this.opts.debug) {
        console.warn("[signal] Sync error:", e);
      }
    } finally {
      this.inflight = false;
      this.tick();
    }
  }
}
